import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Search, Package, Clock, Truck, CheckCircle, XCircle } from "lucide-react";
import SEO from "@/components/SEO";

const STATUS = {
    pending: { label: "অপেক্ষমান", color: "bg-yellow-100 text-yellow-800", icon: Clock },
    confirmed: { label: "নিশ্চিত", color: "bg-blue-100 text-blue-800", icon: CheckCircle },
    shipped: { label: "প্রেরিত", color: "bg-purple-100 text-purple-800", icon: Truck },
    delivered: { label: "ডেলিভার্ড", color: "bg-green-100 text-green-800", icon: CheckCircle },
    cancelled: { label: "বাতিল", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function TrackOrder() {
    const { t, lang } = useI18n();
    const [phone, setPhone] = useState("");
    const [orderNum, setOrderNum] = useState("");
    const [order, setOrder] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setOrder(null);
        try {
            const { data } = await api.get(`/orders/track`, { params: { phone, order_number: orderNum } });
            setOrder(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const sInfo = order ? STATUS[order.status] || STATUS.pending : null;
    const Icon = sInfo?.icon;

    return (
        <div>
            <SEO title={t("track_order")} />
            <section className="bg-hero-grad py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Package className="w-12 h-12 mx-auto text-brand-royal" />
                    <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mt-3">{t("track_order")}</h1>
                    <p className="text-brand-navy/60 mt-2">{lang === "bn" ? "ফোন নম্বর ও অর্ডার নাম্বার দিয়ে আপনার অর্ডার ট্র্যাক করুন" : "Track your order using phone number and order ID"}</p>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <form onSubmit={submit} className="bg-white border border-brand-light rounded-2xl p-6 grid sm:grid-cols-3 gap-3">
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder={t("track_phone")} data-testid="order-track-phone"
                        className="px-4 py-3 rounded-lg border border-brand-light focus:outline-none focus:ring-2 focus:ring-brand-royal/30 sm:col-span-1" />
                    <input value={orderNum} onChange={(e) => setOrderNum(e.target.value)} required placeholder={t("track_number")} data-testid="order-track-input"
                        className="px-4 py-3 rounded-lg border border-brand-light focus:outline-none focus:ring-2 focus:ring-brand-royal/30 sm:col-span-1" />
                    <button disabled={busy} className="btn-primary px-5 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="track-submit">
                        <Search className="w-4 h-4" /> {t("track_btn")}
                    </button>
                </form>

                {order && sInfo && (
                    <div className="mt-8 bg-white border border-brand-light rounded-2xl p-6" data-testid="order-track-result">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="text-xs text-brand-navy/60">অর্ডার নম্বর</div>
                                <div className="text-xl font-bold text-brand-royal">{order.order_number}</div>
                            </div>
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${sInfo.color}`}>
                                <Icon className="w-4 h-4" /> {sInfo.label}
                            </div>
                        </div>
                        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                            <div><span className="text-brand-navy/60">গ্রাহক:</span> <span className="font-medium">{order.customer_name}</span></div>
                            <div><span className="text-brand-navy/60">ফোন:</span> <span className="font-medium">{order.phone}</span></div>
                            <div><span className="text-brand-navy/60">ঠিকানা:</span> <span className="font-medium">{order.address}, {order.zilla}, {order.division}</span></div>
                            <div><span className="text-brand-navy/60">পেমেন্ট:</span> <span className="font-medium">{order.payment_method.toUpperCase()}</span></div>
                            {order.library_name && <div className="sm:col-span-2"><span className="text-brand-navy/60">📚 লাইব্রেরি:</span> <span className="font-medium">{order.library_name}</span></div>}
                        </div>
                        <div className="mt-5">
                            <h4 className="font-semibold text-brand-navy">আইটেম:</h4>
                            <div className="mt-2 space-y-2">
                                {order.items.map((it, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm border border-brand-light p-3 rounded-lg">
                                        <img src={it.cover_image} alt="" className="w-10 h-12 rounded object-cover" />
                                        <div className="flex-1">{it.title}</div>
                                        <div>× {it.quantity}</div>
                                        <div className="font-semibold w-20 text-right">৳{it.price * it.quantity}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-4 border-t border-brand-light pt-3 flex justify-between font-bold text-brand-royal">
                            <span>মোট</span><span>৳{order.total}</span>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
